import React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Stack,
  FormControlLabel,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import { BORDER_RADIUS } from "../../theme/theme";
import {
  EMAIL_REGEX,
  PHONE_REGEX,
  ABN_REGEX,
  WEBSITE_REGEX,
  VAL_MSG,
} from "../utils/validation";

export default function ManageOrgsPanel(props: any) {
  const {
    filteredData,
    editingData,
    setEditingData,
    searchText,
    setSearchText,
    openRow,
    isGlobalAdmin,
    isImpersonating,
    enterImpersonation,
    login,
    organisationList = [],
    handleClickOrganisation,
    handleExpand,
    handleInputChange,
    handleSave,
    handleReassignOrganisation,
    handleCancel,
    handleDelete,
  } = props;

  const [reassigningOrg, setReassigningOrg] = React.useState<any>(null);
  const [selectedParentId, setSelectedParentId] = React.useState("");
  const [isReassigning, setIsReassigning] = React.useState(false);

  const openReassignDialog = (row: any) => {
    setReassigningOrg(row);
    setSelectedParentId(row.parentOrganisationId || "");
  };

  const closeReassignDialog = () => {
    if (isReassigning) return;
    setReassigningOrg(null);
    setSelectedParentId("");
  };

  const confirmReassignment = async () => {
    if (!reassigningOrg) return;
    setIsReassigning(true);
    try {
      await handleReassignOrganisation(reassigningOrg._id, selectedParentId);
      setReassigningOrg(null);
      setSelectedParentId("");
    } finally {
      setIsReassigning(false);
    }
  };

  const eligibleParents = organisationList.filter(
    (candidate: any) =>
      candidate._id !== reassigningOrg?._id &&
      !candidate.parentOrganisationId &&
      candidate.hierarchyRole !== "child",
  );

  return (
    <Box
      id="org-details-tab"
      sx={{ mt: 2, p: 3, bgcolor: "background.paper", borderRadius: BORDER_RADIUS }}
    >
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClickOrganisation}
        >
          CREATE ORGANISATION
        </Button>
      </Box>

      <Box
        id="org-search-bar"
        sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
      >
        <TextField
          label="Search by Organisation Name"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "20px" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{ mt: 1, p: 1 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Hierarchy</strong>
              </TableCell>
              <TableCell>
                <strong>Billing Contact Name</strong>
              </TableCell>
              <TableCell>
                <strong>Billing Contact Email</strong>
              </TableCell>
              <TableCell>
                <strong>Billing Contact Number</strong>
              </TableCell>
              <TableCell>
                <strong>Tiles</strong>
              </TableCell>
              <TableCell>
                <strong>2FA</strong>
              </TableCell>
              <TableCell>
                <strong>Action</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody id="org-table-body">
            {filteredData?.map((row: any) => {
              const realIndex = editingData.findIndex(
                (org: any) => org._id === row._id,
              );
              return (
                <React.Fragment key={row._id}>
                  <TableRow>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {row.parentOrganisationName
                        ? `Child of ${row.parentOrganisationName}`
                        : row.childOrganisationCount > 0
                          ? `Parent · ${row.childOrganisationCount} child${row.childOrganisationCount === 1 ? "" : "ren"}`
                          : "Standalone"}
                    </TableCell>
                    <TableCell>{row.billingContactName}</TableCell>
                    <TableCell>{row.billingContactEmail}</TableCell>
                    <TableCell>{row.billingContactPhone}</TableCell>
                    <TableCell>
                      {Array.isArray(row.access)
                        ? row.access.join(", ")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.twoFactorEnabled || false}
                        disabled
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Organisation" arrow placement="top">
                        <IconButton
                          id={`org-edit-btn-${realIndex}`}
                          color="primary"
                          size="small"
                          onClick={() => handleExpand(realIndex)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {isGlobalAdmin && (
                        <Tooltip
                          title="Reassign organisation"
                          arrow
                          placement="top"
                        >
                          <IconButton
                            aria-label={`Reassign ${row.name}`}
                            color="primary"
                            size="small"
                            onClick={() => openReassignDialog(row)}
                          >
                            <AccountTreeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isGlobalAdmin && !isImpersonating && (
                        <Tooltip
                          title="View as this organisation (bug fix mode)"
                          arrow
                          placement="top"
                        >
                          <IconButton
                            aria-label="View as this organisation (bug fix mode)"
                            color="secondary"
                            size="small"
                            onClick={() => void enterImpersonation(row._id)}
                          >
                            <ArrowForwardIosIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isGlobalAdmin && (
                        <Tooltip
                          title="Delete Organisation"
                          arrow
                          placement="top"
                        >
                          <IconButton
                            onClick={() => handleDelete(row._id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>

                  {openRow === realIndex && (
                    <TableRow>
                      <TableCell colSpan={8} style={{ padding: 0 }}>
                        <Collapse in timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: "#f8f8f8" }}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gap: 2,
                              }}
                            >
                              {/* Organisation Name */}
                              <TextField
                                id={`org-edit-name-${realIndex}`}
                                label="Organisation Name"
                                value={editingData[realIndex].name}
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "name",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].name
                                    ? !/[a-zA-Z]/.test(
                                        editingData[realIndex].name,
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].name &&
                                  !/[a-zA-Z]/.test(editingData[realIndex].name)
                                    ? "Organisation name cannot be purely numerical"
                                    : ""
                                }
                              />

                              {/* ABN */}
                              <TextField
                                label="ABN"
                                value={editingData[realIndex].abn}
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "abn",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].abn
                                    ? !ABN_REGEX.test(
                                        editingData[realIndex].abn.replace(
                                          /\s/g,
                                          "",
                                        ),
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].abn &&
                                  !ABN_REGEX.test(
                                    editingData[realIndex].abn.replace(
                                      /\s/g,
                                      "",
                                    ),
                                  )
                                    ? VAL_MSG.abn
                                    : ""
                                }
                                inputProps={{ maxLength: 11 }}
                              />

                              {/* AFSL Number */}
                              <TextField
                                label="Australian Financial Services License Number"
                                value={
                                  editingData[realIndex].afslNumber || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "afslNumber",
                                    e.target.value,
                                  )
                                }
                              />

                              {/* Billing Contact Name */}
                              <TextField
                                id={`org-edit-contact-name-${realIndex}`}
                                label="Billing Contact Name"
                                value={
                                  editingData[realIndex].billingContactName
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "billingContactName",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].billingContactName
                                    ? !/[a-zA-Z]/.test(
                                        editingData[realIndex]
                                          .billingContactName,
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].billingContactName &&
                                  !/[a-zA-Z]/.test(
                                    editingData[realIndex].billingContactName,
                                  )
                                    ? "Billing contact name cannot be purely numerical"
                                    : ""
                                }
                              />

                              {/* Billing Contact Email */}
                              <TextField
                                label="Billing Contact Email"
                                value={
                                  editingData[realIndex].billingContactEmail
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "billingContactEmail",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].billingContactEmail
                                    ? !EMAIL_REGEX.test(
                                        editingData[realIndex]
                                          .billingContactEmail,
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].billingContactEmail &&
                                  !EMAIL_REGEX.test(
                                    editingData[realIndex].billingContactEmail,
                                  )
                                    ? VAL_MSG.email
                                    : ""
                                }
                              />

                              {/* Billing Contact Phone */}
                              <TextField
                                label="Billing Contact Phone"
                                value={
                                  editingData[realIndex].billingContactPhone
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "billingContactPhone",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].billingContactPhone
                                    ? !PHONE_REGEX.test(
                                        editingData[realIndex]
                                          .billingContactPhone,
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].billingContactPhone &&
                                  !PHONE_REGEX.test(
                                    editingData[realIndex].billingContactPhone,
                                  )
                                    ? VAL_MSG.phone
                                    : ""
                                }
                              />

                              {/* Billing Contact Mobile */}
                              <TextField
                                label="Billing Contact Mobile"
                                value={
                                  editingData[realIndex].billingContactMobile
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "billingContactMobile",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].billingContactMobile
                                    ? !PHONE_REGEX.test(
                                        editingData[realIndex]
                                          .billingContactMobile,
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].billingContactMobile &&
                                  !PHONE_REGEX.test(
                                    editingData[realIndex].billingContactMobile,
                                  )
                                    ? VAL_MSG.mobile
                                    : ""
                                }
                              />

                              {/* Billing Address */}
                              <TextField
                                label="Billing Address"
                                value={
                                  editingData[realIndex].billingAddress?.line1 ||
                                  ""
                                }
                                onChange={(e) => {
                                  const updated = [...editingData];
                                  updated[realIndex] = {
                                    ...updated[realIndex],
                                    billingAddress: {
                                      ...(updated[realIndex].billingAddress ||
                                        {}),
                                      line1: e.target.value,
                                    },
                                  };
                                  setEditingData(updated);
                                }}
                              />

                              {/* Website */}
                              <TextField
                                label="Website"
                                value={editingData[realIndex].website}
                                onChange={(e) =>
                                  handleInputChange(
                                    realIndex,
                                    "website",
                                    e.target.value,
                                  )
                                }
                                error={
                                  editingData[realIndex].website
                                    ? !WEBSITE_REGEX.test(
                                        editingData[realIndex].website.trim(),
                                      )
                                    : false
                                }
                                helperText={
                                  editingData[realIndex].website &&
                                  !WEBSITE_REGEX.test(
                                    editingData[realIndex].website.trim(),
                                  )
                                    ? "Please enter a valid website (e.g. www.example.com)"
                                    : ""
                                }
                              />

                              {login?.data?.role === "global admin" && (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ pl: 1 }}
                                >
                                  <Tooltip title="Toggle to grant free access and bypass all payment requirements for this organisation.">
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      sx={{ pl: 1 }}
                                    >
                                      <Typography variant="body1">
                                        Billing Exempt (Dev/Partner)
                                      </Typography>
                                      <Switch
                                        checked={
                                          editingData[realIndex]
                                            .isBillingExempt || false
                                        }
                                        onChange={(e) =>
                                          handleInputChange(
                                            realIndex,
                                            "isBillingExempt",
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </Stack>
                                  </Tooltip>
                                </Stack>
                              )}
                              <Box
                                sx={{
                                  mb: 2,
                                  display: "flex",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Switch
                                      id={`org-edit-2fa-${realIndex}`}
                                      checked={
                                        editingData[realIndex]
                                          .twoFactorEnabled || false
                                      }
                                      onChange={(e) =>
                                        handleInputChange(
                                          realIndex,
                                          "twoFactorEnabled",
                                          e.target.checked,
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label="2FA Enabled"
                                  labelPlacement="start"
                                  sx={{ ml: 0 }}
                                />
                              </Box>
                            </Box>

                            <Box
                              sx={{ mt: 2, display: "flex", gap: 2 }}
                            >
                              <Button
                                variant="contained"
                                onClick={() => handleSave(realIndex)}
                              >
                                Save
                              </Button>
                              <Button
                                id={`org-edit-cancel-btn-${realIndex}`}
                                variant="outlined"
                                onClick={handleCancel}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(reassigningOrg)}
        onClose={closeReassignDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reassign organisation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Choose the parent organisation for <strong>{reassigningOrg?.name}</strong>.
            Existing users and data will remain in this organisation.
          </DialogContentText>
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 2,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <AccountTreeIcon color="primary" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current hierarchy
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {reassigningOrg?.parentOrganisationName
                  ? `Child of ${reassigningOrg.parentOrganisationName}`
                  : "Standalone organisation"}
              </Typography>
            </Box>
          </Box>
          <FormControl fullWidth>
            <Typography
              component="label"
              htmlFor="reassign-parent-select"
              variant="body2"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              New parent organisation
            </Typography>
            <Select
              id="reassign-parent-select"
              value={selectedParentId}
              onChange={(event) => setSelectedParentId(event.target.value)}
              displayEmpty
              inputProps={{ "aria-label": "New parent organisation" }}
              renderValue={(value) => {
                if (!value) return "Standalone — no parent";
                return (
                  eligibleParents.find(
                    (candidate: any) => candidate._id === value,
                  )?.name || "Select an organisation"
                );
              }}
            >
              <MenuItem value="">
                Standalone — no parent
              </MenuItem>
              {eligibleParents.map((candidate: any) => (
                <MenuItem key={candidate._id} value={candidate._id}>
                  {candidate.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Select an organisation to make it the parent, or choose standalone
              to remove the existing relationship.
            </Typography>
          </FormControl>
          {reassigningOrg?.childOrganisationCount > 0 && selectedParentId && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              A parent organisation with children cannot itself become a child.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={closeReassignDialog}
            disabled={isReassigning}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmReassignment()}
            disabled={
              isReassigning ||
              selectedParentId === (reassigningOrg?.parentOrganisationId || "") ||
              Boolean(reassigningOrg?.childOrganisationCount > 0 && selectedParentId)
            }
          >
            {isReassigning ? "Reassigning…" : "Confirm reassignment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
